<template>

  <div class="horizontal-donut-chart-widget custom-widget">

    <div class="body">

      <div class="data-wrapper">
        <div class="labels-wrapper">
          <span class="is-bold mb-3" :class="{warning: data.warning, 'normal-color': !data.warning}"> {{title}} </span>
          <div v-if="hasData" v-for="index in labels.length" :key="index">
            <span class="dataset-label-color" :style="`background-color: ${datasetColors[index - 1]}`"></span>
            <span class="dataset-label">{{labels[index - 1]}} ({{datasetData[index - 1]}})</span>
            &nbsp;
            <span v-if="!hidePercentage" class="dataset-label-info">({{datasetData[index - 1]}}<span class="d-none d-xl-inline-block">- {{percentage(datasetData[index - 1], total).toFixed(2)}}%</span>)</span>
          </div>
        </div>
        <div v-if="hasData" class="chart-wrapper">
          <va-chart class="chart" :data="data" :options="chartOptions" type="donut"/>
        </div>
        <div v-else class="no-data-wrapper">
          <h6><b>{{ $t('horizontalDonutChartWidget.no_data_for_chart') }}</b></h6>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  props: {
    hidePercentage: {
      type: Boolean,
      required: false,
      default: true
    },
    title: {
      type: String,
      required: true
    },
    options: {
      required: false,
    },
    data: {
      required: true
    },
  },
  computed: {
    datasetData: function () {
      if (this.data !== undefined) {
        return this.data.datasets[0].data
      }
    },
    datasetColors: function () {
      if (this.data !== undefined) {
        return this.data.datasets[0].backgroundColor
      }
    },
    labels: function () {
      if (this.data !== undefined) {
        return this.data.labels
      }
    },
    total: function () {
      return this.datasetData !== undefined ? this.datasetData.reduce((cummulative, value) => cummulative + value, 0) : 0
    },
    hasData: function () {
      return this.data !== undefined && this.data !== null && this.total > 0
    },
    chartOptions: function () {
      let options = {
        cutoutPercentage: 55,
        width: 90,
        height: 90,
        legend: {
          display: false
        },
        tooltips: {
          enabled: false,
          custom: function (tooltipModel) {
            let tooltipEl = document.getElementById('chartjs-tooltip')
            if (!tooltipEl) {
              tooltipEl = document.createElement('div')
              tooltipEl.id = 'chartjs-tooltip'
              tooltipEl.style.position = 'absolute'
              tooltipEl.style.pointerEvents = 'none'
              tooltipEl.style.background = 'rgb(0,0,0)'
              tooltipEl.style.color = '#fff'
              tooltipEl.style.borderRadius = '4px'
              tooltipEl.style.padding = '6px 8px'
              tooltipEl.style.transition = 'all .1s ease'
              tooltipEl.style.fontSize = '14px'
              document.body.appendChild(tooltipEl)
            }

            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0
              return
            }

            const position = this._chart.canvas.getBoundingClientRect()

            tooltipEl.innerHTML = (tooltipModel.body || [])
              .map(b => b.lines.join('<br>'))
              .join('<br>')

            tooltipEl.style.opacity = 1
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px'
            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px'
          }
        },
        responsive: true,
        maintainAspectRatio: true
      }
      if (this.options !== undefined && this.options !== null) {
        Object.assign(options, this.options)
      }
      return options
    }
  },
  methods: {
    percentage: function (count, total) {
      return count / total * 100
    },
  }
}
</script>

<style lang="scss" scoped>

$card-color: #F0F0F0;
$primary-dark-gray: #48494B;

.va-chart {
  align-items: flex-start !important;
  padding-top: 35px;
}

.horizontal-donut-chart-widget {
  //padding: 1rem;
  padding-left: 5px;
  background-color: $card-color;
  border-radius: 10px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);

  .header {
    color: $primary-dark-gray;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: solid 2px #d3d3d3;
  }

  .data-wrapper {
    display: flex;
    justify-content: space-between;
    margin-left: 0.5rem;
    padding-bottom: 2rem;
  }

  .dataset-label-color {
    display: inline-block;
    width: 0.9rem;
    height: 0.9rem;
    border-radius: 0.45rem;
    margin-right: 0.5em
  }

  .dataset-label {
    //font-size: 0.85rem;
    font-weight: bold;
  }

  .dataset-label-info {
    font-size: 0.9rem;
    font-weight: 300;
  }

  .dataset-label, .dataset-label-info {
    color: #6d767f;
  }

  .chart-wrapper {
    max-width: 35%;
    margin-top: 1rem;
    margin-bottom: 0.4rem;
    margin-right: 7.5%;
  }

  .labels-wrapper {
    width: 50%;
    max-width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1rem 0;
    font-size: 14px;
  }

  .no-data-wrapper {
    width: 100%;
    min-height: 140px;
    max-width: 100%;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media only screen and (max-width: 768px) {
    .data-wrapper {
      flex-direction: column;
    }

    .labels-wrapper, .no-data-wrapper {
      width: 100%;
      max-width: 100%;
    }

    .chart-wrapper {
      width: 80%;
      max-width: 80%;
      margin-top: 1rem;
      margin-left: 10%;
    }

    .labels-wrapper {
      margin-top: 1rem;
    }

    .header {
      flex-direction: column;
      text-align: center;
    }
  }
}

.custom-widget {
  height: calc(100% - 24px);
}

.warning {
  color: #ca6159
}

.normal-color {
  color: #a4a4a4
}

</style>
<style lang="scss">
#chartjs-tooltip {
  z-index: 9999;
  white-space: nowrap;
}
.chart-wrapper {
  overflow: visible !important;
}
</style>
